import { createSelector } from "reselect";
import { createAction } from "redux-actions";
import { trackPageChange } from "~/libs/matomo";
import { path, pathOr, ifElse, includes, without, append, __, equals } from "ramda";
import { MATERIAL_TYPES } from "~/constants/materials";

import { DLS_MODE, STUDENT_MODE, PRESENTATION_MODE } from "~/constants/presentation-modes";

import { selectUsersettingsConfig } from "~/store/ducks/domain/config";
import { cleanUsersettings } from "~/utils";

/* Actions */
export const SET_ABSOLUTE_PAGE_NUMBER = "USER@PAGE::SET_ABSOLUTE";
export const OPEN_LEFT_SIDEBAR = "USER@LEFT_SIDEBAR::OPEN";
export const CLOSE_LEFT_SIDEBAR = "USER@LEFT_SIDEBAR::CLOSE";
export const SWITCH_TAB_LEFT_SIDEBAR = "USER@LEFT_SIDEBAR::SWITCH_TAB";
export const OPEN_RIGHT_SIDEBAR = "USER@RIGHT_SIDEBAR::OPEN";
export const CLOSE_RIGHT_SIDEBAR = "USER@RIGHT_SIDEBAR::CLOSE";
export const SWITCH_TAB_RIGHT_SIDEBAR = "USER@RIGHT_SIDEBAR::SWITCH_TAB";
export const TOGGLE_MENU_ITEM_RIGHT_SIDEBAR = "USER@RIGHT_SIDEBAR::TOGGLE_MENU_ITEM";
export const SHOW_ANNOTATIONS = "USER@UI/ANNOTATIONS::SHOW";
export const HIDE_ANNOTATIONS = "USER@UI/ANNOTATIONS::HIDE";
export const SET_DISPLAY_MODE = "USER@UI::SET_DISPLAY_MODE";
export const SET_USERSETTINGS = "USER@UI::SET_USERSETTINGS";

export const AUDIO_PLAYBACK_RATE = "USER@UI::PLAYBACKRATE";
export const TOGGLE_AUDIO_PLAYBACK_RATE = "USER@UI::TOGGLE_PLAYBACKRATE";

export const DLS_SWITCH_TAB = "USER@UI::DLS_SWITCH_TAB";
export const DLS_CATEGORY_PAGE_SET = "USER@UI::DLS_CATEGORY_PAGE";
export const DLS_CATEGORY_BOOK_SET = "USER@UI::DLS_CATEGORY_BOOK";
export const DLS_CATEGORY_SET = "USER@UI::DLS_SET_CATEGORY";

export const PRESENTATION_MATERIAL_SHOW = "USER@PRESENTATION::MATERIAL_SHOW";
export const PRESENTATION_MATERIAL_HIDE = "USER@PRESENTATION::MATERIAL_HIDE";

export const SHOW_CUTOUT = "USER@CUTOUT::SHOW";
export const HIDE_CUTOUT = "USER@CUTOUT::HIDE";
export const SET_CUTOUT_SIZE = "USER@CUTOUT::SET_CUTOUT_SIZE";
export const SET_CUTOUT_POSITION = "USER@CUTOUT::SET_CUTOUT_POSITION";
export const SHOW_MODAL_CUTOUT = "USER@MODAL_CUTOUT::SHOW";
export const HIDE_MODAL_CUTOUT = "USER@MODAL_CUTOUT::HIDE";
export const SET_MODAL_CUTOUT_SIZE = "USER@CUTOUT::SET_MODAL_CUTOUT_SIZE";
export const SET_MODAL_CUTOUT_POSITION = "USER@CUTOUT::SET_MODAL_CUTOUT_POSITION";

export const setDlsActiveTab = createAction(DLS_SWITCH_TAB);
export const setDlsCategoryPage = createAction(DLS_CATEGORY_PAGE_SET);
export const setDlsCategoryBook = createAction(DLS_CATEGORY_BOOK_SET);

export const setDlsActiveCategory = category => (dispatch, getState) => {
  const tab = selectDlsActiveTab(getState());

  if (tab === "all") {
    dispatch(setDlsCategoryBook(category));
  } else if (tab === "page") {
    dispatch(setDlsCategoryPage(category));
  }
};

/* Action Creators */
export const setAbsolutePage = createAction(SET_ABSOLUTE_PAGE_NUMBER);

export const openSidebarLeft = createAction(OPEN_LEFT_SIDEBAR);
export const closeSidebarLeft = createAction(CLOSE_LEFT_SIDEBAR);
export const switchTabOnSidebarLeft = createAction(SWITCH_TAB_LEFT_SIDEBAR);
export const toggleSidebarLeft = () => (dispatch, getState) => {
  const isOpen = selectLeftSidebarIsOpen(getState());
  dispatch(isOpen ? closeSidebarLeft() : openSidebarLeft());
};

export const openSidebarRight = createAction(OPEN_RIGHT_SIDEBAR);
export const closeSidebarRight = createAction(CLOSE_RIGHT_SIDEBAR);
export const switchTabOnSidebarRight = createAction(SWITCH_TAB_RIGHT_SIDEBAR);
export const toggleSidebarRightMenuItem = createAction(TOGGLE_MENU_ITEM_RIGHT_SIDEBAR);
export const toggleSidebarRight = () => (dispatch, getState) => {
  const isOpen = selectRightSidebarIsOpen(getState());
  dispatch(isOpen ? closeSidebarRight() : openSidebarRight());
};

export const showAnnotations = createAction(SHOW_ANNOTATIONS);
export const hideAnnotations = createAction(HIDE_ANNOTATIONS);
export const toggleAnnotations = () => (dispatch, getState) => {
  const isVisible = selectIsAnnotationsVisible(getState());
  dispatch(isVisible ? hideAnnotations() : showAnnotations());
};
export const toggleAudioPlaybackRate = createAction(TOGGLE_AUDIO_PLAYBACK_RATE);

export const setDisplayMode = createAction(SET_DISPLAY_MODE);
export const setUsersettings = createAction(SET_USERSETTINGS);

export const showPresentationMaterial = createAction(PRESENTATION_MATERIAL_SHOW);
export const hidePresentationMaterial = createAction(PRESENTATION_MATERIAL_HIDE);

export const showCutout = createAction(SHOW_CUTOUT);
export const hideCutout = createAction(HIDE_CUTOUT);
export const showModalCutout = createAction(SHOW_MODAL_CUTOUT);
export const hideModalCutout = createAction(HIDE_MODAL_CUTOUT);
export const setCutoutSize = createAction(SET_CUTOUT_SIZE);
export const setCutoutPosition = createAction(SET_CUTOUT_POSITION);
export const setModalCutoutSize = createAction(SET_MODAL_CUTOUT_SIZE);
export const setModalCutoutPosition = createAction(SET_MODAL_CUTOUT_POSITION);

const getUiState = path(["user", "uistate"]);

export const selectAbsolutePage = createSelector(getUiState, uiState => {
  const page = path(["absolute"], uiState);
  return parseInt(page, 10);
});

export const selectLeftSidebarIsOpen = createSelector(getUiState, path(["leftSidebar_isOpen"]));

export const selectLeftSidebarActiveTab = createSelector(getUiState, path(["leftSidebar_activeTab"]));

export const selectRightSidebarIsOpen = createSelector(getUiState, path(["rightSidebar_isOpen"]));

export const selectRightSidebarActiveTab = createSelector(getUiState, path(["rightSidebar_activeTab"]));

export const selectRightSidebarActiveItems = createSelector(getUiState, path(["rightSidebar_activeItems"]));

export const selectIsAnnotationsVisible = createSelector(getUiState, path(["isVisible"]));

export const selectAudioPlaybackRate = createSelector(getUiState, path(["audioPlaybackRate"]));

export const selectShowCutout = createSelector(getUiState, path(["showCutout"]));
export const selectCutoutSize = createSelector(getUiState, path(["cutoutSize"]));
export const selectCutoutPosition = createSelector(getUiState, path(["cutoutPosition"]));

export const selectShowModalCutout = createSelector(getUiState, path(["showModalCutout"]));
export const selectModalCutoutSize = createSelector(getUiState, path(["modalCutoutSize"]));
export const selectModalCutoutPosition = createSelector(getUiState, path(["modalCutoutPosition"]));

export const selectDisplayMode = createSelector(getUiState, path(["displayMode"]));

export const selectIsStudentActive = createSelector(selectDisplayMode, equals(STUDENT_MODE));

export const selectIsDlsActive = createSelector(selectDisplayMode, equals(DLS_MODE));

export const selectIsPresentationActive = createSelector(selectDisplayMode, equals(PRESENTATION_MODE));

export const selectDlsActiveTab = createSelector(getUiState, path(["rightSidebar_dlsActiveTab"]));

export const selectDlsActivePageCategory = createSelector(getUiState, path(["rightSidebar_dlsActivePageCategory"]));

export const selectDlsActiveBookCategory = createSelector(getUiState, path(["rightSidebar_dlsActiveBookCategory"]));

export const selectDlsActiveCategoryTitle = createSelector(
  selectDlsActiveTab,
  selectDlsActivePageCategory,
  selectDlsActiveBookCategory,
  (tab, pageCategory, bookCategory) => {
    if (tab === "all") {
      return bookCategory;
    }
    if (tab === "page") {
      return pageCategory;
    }

    return null;
  }
);

export const selectUsersettingsSaved = createSelector(getUiState, path(["usersettings"]));

export const selectUsersettings = createSelector(selectUsersettingsSaved, selectUsersettingsConfig, (saved, config) => {
  return config.map(setting => {
    const savedSetting = saved.find(_setting => _setting.label === setting.label);
    return {
      ...setting,
      items: setting.items.map(item => {
        if (!savedSetting) return { ...item, active: item.default };
        const savedItem = savedSetting.items.find(_item => _item.label === item.label);
        return {
          ...item,
          active: savedItem ? savedItem.active : item.default
        };
      })
    };
  });
});

export const selectUsersettingsDlsFilters = createSelector(selectUsersettings, usersettings =>
  usersettings.flatMap(setting =>
    setting.items.map(item => ({
      filter: item.dlsfilter,
      active: item.active
    }))
  )
);

export const selectIsPresentationMaterialVisible = createSelector(
  getUiState,
  path(["rightSidebar_isPresentationMaterialVisible"])
);

export const toggleIsPresentationMaterialVisible = () => (dispatch, getState) => {
  const isVisible = selectIsPresentationMaterialVisible(getState());
  dispatch(isVisible ? hidePresentationMaterial() : showPresentationMaterial());
};

const isDls = () => pathOr(false, ["book", "settings", "features", "dls"], window.getClientConfiguration());

export const toggleCutoutShow = () => (dispatch, getState) => {
  const isVisible = selectShowCutout(getState());
  dispatch(isVisible ? hideCutout() : showCutout());
};
export const toggleModalCutoutShow = () => (dispatch, getState) => {
  const isVisible = selectShowModalCutout(getState());
  dispatch(isVisible ? hideModalCutout() : showModalCutout());
};
// Initial State
const initialState = () => ({
  // pageIndex
  absolute: 1,
  leftSidebar_isOpen: pathOr(true, ["book", "initial", "sidebarLeft", "isOpen"], window.getClientConfiguration()),
  leftSidebar_activeTab: pathOr(
    isDls() ? "outline" : "pages",
    ["book", "initial", "sidebarLeft", "defaultTab"],
    window.getClientConfiguration()
  ),
  rightSidebar_isOpen: pathOr(true, ["book", "initial", "sidebarRight", "isOpen"], window.getClientConfiguration()),
  rightSidebar_activeTab: pathOr(
    "all",
    ["book", "initial", "sidebarRight", "defaultTab"],
    window.getClientConfiguration()
  ),
  rightSidebar_activeItems: MATERIAL_TYPES,
  isVisible: true,
  displayMode: pathOr(
    isDls() ? DLS_MODE : STUDENT_MODE,
    ["book", "initial", "displayMode"],
    window.getClientConfiguration()
  ),
  rightSidebar_dlsActiveTab: pathOr(
    "all",
    ["book", "initial", "sidebarRight", "dlsDefaultTab"],
    window.getClientConfiguration()
  ),
  rightSidebar_dlsActivePageCategory: null,
  rightSidebar_dlsActiveBookCategory: null,
  rightSidebar_isPresentationMaterialVisible: true,
  usersettings: [],
  audioPlaybackRate: 1,
  showCutout: false,
  cutoutSize: null,
  cutoutPosition: null,
  showModalCutout: false,
  modalCutoutSize: null,
  modalCutoutPosition: null
});

// Reducer
export default function reducer(state = initialState(), action = {}) {
  const toggleItem = ifElse(
    includes(__, state.rightSidebar_activeItems),
    without(__, state.rightSidebar_activeItems),
    append(__, state.rightSidebar_activeItems)
  );

  switch (action.type) {
    case SET_ABSOLUTE_PAGE_NUMBER:
      trackPageChange(action.payload);
      return { ...state, absolute: action.payload };
    case OPEN_LEFT_SIDEBAR:
      return { ...state, leftSidebar_isOpen: true };
    case CLOSE_LEFT_SIDEBAR:
      return { ...state, leftSidebar_isOpen: false };
    case SWITCH_TAB_LEFT_SIDEBAR:
      return { ...state, leftSidebar_activeTab: action.payload };
    case OPEN_RIGHT_SIDEBAR:
      return { ...state, rightSidebar_isOpen: true };
    case CLOSE_RIGHT_SIDEBAR:
      return { ...state, rightSidebar_isOpen: false };
    case SWITCH_TAB_RIGHT_SIDEBAR:
      return { ...state, rightSidebar_activeTab: action.payload };
    case TOGGLE_MENU_ITEM_RIGHT_SIDEBAR:
      return { ...state, rightSidebar_activeItems: toggleItem(action.payload) };
    case SHOW_ANNOTATIONS:
      return { ...state, isVisible: true };
    case HIDE_ANNOTATIONS:
      return { ...state, isVisible: false };
    case SET_DISPLAY_MODE:
      return { ...state, displayMode: action.payload };
    case SET_USERSETTINGS:
      return { ...state, usersettings: cleanUsersettings(action.payload) };
    case DLS_SWITCH_TAB:
      return { ...state, rightSidebar_dlsActiveTab: action.payload };
    case DLS_CATEGORY_PAGE_SET:
      return { ...state, rightSidebar_dlsActivePageCategory: action.payload };
    case DLS_CATEGORY_BOOK_SET:
      return { ...state, rightSidebar_dlsActiveBookCategory: action.payload };
    case PRESENTATION_MATERIAL_SHOW:
      return { ...state, rightSidebar_isPresentationMaterialVisible: true };
    case PRESENTATION_MATERIAL_HIDE:
      return { ...state, rightSidebar_isPresentationMaterialVisible: false };
    case TOGGLE_AUDIO_PLAYBACK_RATE:
      return { ...state, audioPlaybackRate: state.audioPlaybackRate === 1 ? 0.6 : 1 };
    case HIDE_CUTOUT:
      return { ...state, showCutout: false };
    case SHOW_CUTOUT:
      return { ...state, showCutout: true };
    case HIDE_MODAL_CUTOUT:
      return { ...state, showModalCutout: false };
    case SHOW_MODAL_CUTOUT:
      return { ...state, showModalCutout: true };
    case SET_CUTOUT_SIZE:
      return { ...state, cutoutSize: action.payload };
    case SET_CUTOUT_POSITION:
      return { ...state, cutoutPosition: action.payload };
    case SET_MODAL_CUTOUT_SIZE:
      return { ...state, modalCutoutSize: action.payload };
    case SET_MODAL_CUTOUT_POSITION:
      return { ...state, modalCutoutPosition: action.payload };

    default:
      return state;
  }
}
